import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { FileUpload } from 'primeng/fileupload';
import { AttachmentService } from 'src/app/core/data-services/attachments/attachment.service';
import { AuthorizedPersonDto } from 'src/app/core/data-models/authorized-person.data.models';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import {
  AttachmentUploadRequest,
  AttachmentUploadResponse,
  FundDataService,
} from 'src/app/core/data-services/fund/fund.data.service';

@Component({
  selector: 'app-applicant-step1-compliance',
  templateUrl: './applicant-step1-compliance.component.html',
  styleUrls: ['./applicant-step1-compliance.component.css'],
})
export class ApplicantStep1ComplianceComponent implements OnInit, OnChanges {
  @Input() isDetails = false;
  /** Optional seed from fund first-step endpoint. */
  @Input() formData?: Partial<AuthorizedPersonDto> | null;
  @ViewChild('fileUploader') fileUploader!: FileUpload;

  form!: FormGroup;
  selectedFileName = '';
  uploadedStoredFileId: string | null = null;
  uploadedPreviewPath: string | null = null;
  uploadedFileUrl: string | null = null;

  isUploading = false;
  isDeleting = false;
  isPreviewing = false;
  uploadErrorMessage = '';

  constructor(
    private fb: FormBuilder,
    private fundDataService: FundDataService,
    private attachmentService: AttachmentService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const mustPickYesNo: ValidatorFn = (c: AbstractControl): ValidationErrors | null =>
      c.value === 'yes' || c.value === 'no' ? null : { required: true };

    this.form = this.fb.group({
      sameAsOwner: [null as string | null, mustPickYesNo],
      politicalExposed: [null as string | null, mustPickYesNo],
      usCitizenBoardOrSeniorManagement: [null as string | null, mustPickYesNo],
      letter: [''],
      authorizationDocumentStoredFileId: [''],
      authorizationDocumentPreviewPath: [''],
      authorizationDocumentFileName: [''],
      authorizationDocumentFileUrl: [''],
    });
    if (this.isDetails) {
      this.form.disable();
    }
    this.patchFormFromFundData(this.formData);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) return;
    if (changes['formData']) {
      this.patchFormFromFundData(changes['formData'].currentValue as Partial<AuthorizedPersonDto> | null | undefined);
    }
  }

  async onFileUpload(event: { files?: File[] }): Promise<void> {
    const file = event.files?.[0];
    if (!file) return;
    this.uploadErrorMessage = '';

    const ext = this.getFileExtension(file.name);
    const allowed = ['.pdf', '.png', '.jpg', '.jpeg'];
    if (!allowed.includes(ext)) {
      this.uploadErrorMessage = 'fundApplication.uploadHintPdfImage';
      this.notificationService.error('fundApplication.uploadHintPdfImage');
      this.clearFileInputOnly();
      return;
    }

    this.selectedFileName = file.name;
    this.isUploading = true;
    try {
      const uploaded = await this.attachmentService.uploadFile(file, 100);
      const uploadBody: AttachmentUploadRequest = {
        storedFileId: uploaded.storedFileId,
        contentType: file.type || this.guessContentType(ext),
        fileExtension: ext,
        parentFolderName: 'FundBorrower',
        attachmentTypeName: 'AgencyAuthorizationLetter',
      };
      const res = await firstValueFrom(this.fundDataService.UploadAttachment(uploadBody));
      if (!res?.success || !res.resource) {
        this.uploadErrorMessage = 'common.ServerError';
        this.notificationService.error('common.SubmissionFailed');
        this.clearFileInputOnly();
        return;
      }
      this.uploadErrorMessage = '';
      this.applyUploadedFileResponse(res.resource, file.name);
    } catch {
      this.uploadErrorMessage = 'common.ServerError';
      this.notificationService.error('common.ServerError');
      this.clearFileInputOnly();
    } finally {
      this.isUploading = false;
    }
  }

  clearFile(): void {
    this.deleteUploadedFile();
  }

  deleteUploadedFile(): void {
    if (this.isDetails || this.isDeleting) return;

    const storedId = this.uploadedStoredFileId || this.form.get('authorizationDocumentStoredFileId')?.value || '';
    if (!storedId) {
      this.clearUploadedState();
      return;
    }

    this.isDeleting = true;
    this.fundDataService.DeleteAgencyAuthorizationLetter(storedId).subscribe({
      next: () => {
        this.uploadErrorMessage = '';
        this.clearUploadedState();
      },
      error: () => {
        this.notificationService.error('common.ServerError');
      },
      complete: () => {
        this.isDeleting = false;
      },
    });
  }

  previewUploadedFile(): void {
    this.openAgencyLetterDownload();
  }

  get agencyLetterPublicDownloadUrl(): string | null {
    return (
      this.pickAbsoluteHttpUrl(this.uploadedFileUrl) ||
      this.pickAbsoluteHttpUrl(this.form.get('authorizationDocumentFileUrl')?.value as string) ||
      null
    );
  }

  get hasStoredAgencyLetter(): boolean {
    const id = (
      this.uploadedStoredFileId ||
      (this.form.get('authorizationDocumentStoredFileId')?.value as string) ||
      ''
    )
      .toString()
      .trim();
    return id.length > 0;
  }

  openAgencyLetterDownload(): void {
    const id = (
      this.uploadedStoredFileId ||
      (this.form.get('authorizationDocumentStoredFileId')?.value as string) ||
      ''
    )
      .toString()
      .trim();
    if (id) {
      this.attachmentService.getViewUrl(id).then((url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
      }).catch(() => this.notificationService.error('common.ServerError'));
      return;
    }
    const url = this.agencyLetterPublicDownloadUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private pickAbsoluteHttpUrl(value: string | null | undefined): string | null {
    if (value == null) return null;
    const u = String(value).trim();
    if (!u) return null;
    if (u.startsWith('https://') || u.startsWith('http://')) return u;
    return null;
  }

  private patchFormFromFundData(data?: Partial<AuthorizedPersonDto> | null): void {
    if (!data) return;
    const toYesNo = (v: unknown): 'yes' | 'no' | null => {
      if (v === true) return 'yes';
      if (v === false) return 'no';
      return null;
    };
    const d = data as Partial<AuthorizedPersonDto> & {
      AuthorizationDocumentStoredFileId?: string | null;
      AuthorizationDocumentPreviewPath?: string | null;
      AuthorizationDocumentFileName?: string | null;
      AuthorizationDocumentFileUrl?: string | null;
    };

    this.form.patchValue({
      sameAsOwner: toYesNo(data.sameAsOwner),
      politicalExposed: toYesNo(data.politicalExposed),
      usCitizenBoardOrSeniorManagement: toYesNo(data.usCitizenBoardOrSeniorManagement),
      letter: data.letter || data.base64Letter || '',
      authorizationDocumentStoredFileId:
        data.authorizationDocumentStoredFileId || d.AuthorizationDocumentStoredFileId || '',
      authorizationDocumentPreviewPath:
        data.authorizationDocumentPreviewPath || d.AuthorizationDocumentPreviewPath || '',
      authorizationDocumentFileName:
        data.authorizationDocumentFileName || d.AuthorizationDocumentFileName || '',
      authorizationDocumentFileUrl:
        data.authorizationDocumentFileUrl || d.AuthorizationDocumentFileUrl || '',
    });

    this.uploadedStoredFileId =
      data.authorizationDocumentStoredFileId || d.AuthorizationDocumentStoredFileId || null;
    this.uploadedPreviewPath =
      data.authorizationDocumentPreviewPath || d.AuthorizationDocumentPreviewPath || null;
    this.uploadedFileUrl = data.authorizationDocumentFileUrl || d.AuthorizationDocumentFileUrl || null;
    if (this.uploadedStoredFileId) {
      this.selectedFileName =
        data.authorizationDocumentFileName ||
        this.extractFileNameFromPreviewPath(this.uploadedPreviewPath) ||
        'Uploaded file';
    } else if (data.letter || data.base64Letter) {
      this.selectedFileName = data.authorizationDocumentFileName || 'Uploaded file';
    }
  }

  getSubmitPayload(): Pick<
    AuthorizedPersonDto,
    | 'sameAsOwner'
    | 'politicalExposed'
    | 'usCitizenBoardOrSeniorManagement'
    | 'letter'
    | 'authorizationDocumentStoredFileId'
  > {
    const v = this.form.getRawValue();
    const yn = (x: unknown): boolean => x === 'yes';
    return {
      sameAsOwner: yn(v.sameAsOwner),
      politicalExposed: yn(v.politicalExposed),
      usCitizenBoardOrSeniorManagement: yn(v.usCitizenBoardOrSeniorManagement),
      letter: '',
      authorizationDocumentStoredFileId:
        (v.authorizationDocumentStoredFileId as string) || this.uploadedStoredFileId || '',
    };
  }

  private applyUploadedFileResponse(response: AttachmentUploadResponse, fallbackName: string): void {
    this.uploadedStoredFileId = response.storedFileId;
    this.uploadedPreviewPath = response.storedFileId || null;
    this.uploadedFileUrl = response.url ?? null;

    const displayName = fallbackName || this.extractFileNameFromPreviewPath(this.uploadedPreviewPath) || 'Uploaded file';
    this.selectedFileName = displayName;
    this.form.patchValue({
      letter: '',
      authorizationDocumentStoredFileId: response.storedFileId,
      authorizationDocumentPreviewPath: this.uploadedPreviewPath || '',
      authorizationDocumentFileName: displayName,
      authorizationDocumentFileUrl: response.url || '',
    });
    this.fileUploader?.clear();
  }

  private clearUploadedState(): void {
    this.uploadedStoredFileId = null;
    this.uploadedPreviewPath = null;
    this.uploadedFileUrl = null;
    this.selectedFileName = '';
    this.form.patchValue({
      letter: '',
      authorizationDocumentStoredFileId: '',
      authorizationDocumentPreviewPath: '',
      authorizationDocumentFileName: '',
      authorizationDocumentFileUrl: '',
    });
    this.fileUploader?.clear();
  }

  private clearFileInputOnly(): void {
    this.fileUploader?.clear();
    if (!this.uploadedStoredFileId) this.selectedFileName = '';
  }

  private getFileExtension(name: string): string {
    const idx = name.lastIndexOf('.');
    if (idx < 0) return '.bin';
    const ext = name.substring(idx).toLowerCase();
    return ext.startsWith('.') ? ext : `.${ext}`;
  }

  private guessContentType(ext: string): string {
    switch ((ext || '').toLowerCase()) {
      case '.pdf':
        return 'application/pdf';
      case '.png':
        return 'image/png';
      case '.jpg':
      case '.jpeg':
        return 'image/jpeg';
      default:
        return 'application/octet-stream';
    }
  }

  private extractFileNameFromPreviewPath(path: string | null | undefined): string {
    if (!path) return '';
    const parts = path.split('/');
    return parts.length > 0 ? parts[parts.length - 1] : '';
  }
}
